import { Component, forwardRef, ChangeDetectionStrategy, input, output, signal, computed, effect } from '@angular/core';

import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type ToggleSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'ui-toggle',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './toggle.component.html',
  styleUrls: ['./toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The public id belongs to the focusable control, not its custom-element host.
  host: { "[attr.id]": "null" },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true
    }
  ]
})
export class ToggleComponent implements ControlValueAccessor {
  // Signal-based inputs
  disabled = input<boolean>(false);
  size = input<ToggleSize>('md');
  id = input<string | undefined>(undefined);
  name = input<string | undefined>(undefined);

  // Accessibility inputs
  ariaLabel = input<string | undefined>(undefined);
  ariaLabelledBy = input<string | undefined>(undefined);
  ariaDescribedBy = input<string | undefined>(undefined);

  /**
   * Back-compat input feeding the internal `_checked` signal.
   * Restored as part of completing the fe-021b-signal-inputs branch
   * because consumer templates still bind `[checked]="..."` and the
   * Angular template strict-mode check refuses bindings without an
   * input declaration. Pair with the `effect()` below to keep the
   * inbound prop in sync with `_checked` (which CVA also writes to).
   */
  checkedInput = input<boolean | undefined>(undefined, { alias: 'checked' });

  // Primary output - standardized naming (FIX 12)
  valueChange = output<boolean>();

  // Legacy output for backward compatibility with existing templates
  // (ml-anomaly-detection auto-action-config + model-management still use it)
  // GAP-FE-021B: signal-based output().
  toggleChange = output<boolean>();

  // Internal checked state as signal
  private _checked = signal(false);

  // Sync external `[checked]` prop into the internal signal.
  private readonly _syncCheckedInput = effect(() => {
    const v = this.checkedInput();
    if (v !== undefined) this._checked.set(v);
  });

  // Internal disabled state from ControlValueAccessor
  private _disabledFromCVA = signal(false);

  /**
   * Backward-compatible `checked` accessor — exposed as a plain getter (not a
   * signal input) because NgModel/ControlValueAccessor drives the underlying
   * `_checked` signal via writeValue(). Templates that read `checked()` will
   * see the current value.
   */
  get checked(): boolean {
    return this._checked();
  }

  /** Backward-compatible `value` accessor — same shape as `checked`. */
  get value(): boolean {
    return this._checked();
  }

  /** Check if disabled from any source */
  get isDisabled(): boolean {
    return this.disabled() || this._disabledFromCVA();
  }

  private onChangeCallback: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  /** Track dimensions based on size - computed for reactivity */
  trackClasses = computed(() => {
    const baseClasses = [
      'relative',
      'inline-flex',
      'shrink-0',
      'cursor-pointer',
      'rounded-full',
      'transition-colors',
      'duration-200',
      'ease-in-out',
      'focus:outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-bb-accent-blue',
      'focus-visible:ring-offset-2',
      'focus-visible:ring-offset-bb-bg-primary'
    ];

    // Size variants
    const sizeClasses: Record<ToggleSize, string[]> = {
      sm: ['w-9', 'h-5'],
      md: ['w-12', 'h-7'],
      lg: ['w-14', 'h-8']
    };

    // State colors
    if (this.isDisabled) {
      baseClasses.push('cursor-not-allowed', 'opacity-50');
      baseClasses.push(this.checked ? 'bg-bb-accent-blue/50' : 'bg-bb-bg-tertiary/50');
    } else {
      baseClasses.push(this.checked ? 'bg-bb-accent-blue' : 'bg-bb-bg-tertiary');
    }

    return [...baseClasses, ...sizeClasses[this.size()]].join(' ');
  });

  /** Thumb (knob) classes - computed for reactivity */
  thumbClasses = computed(() => {
    const baseClasses = [
      'pointer-events-none',
      'inline-block',
      'rounded-full',
      'bg-white',
      'shadow-lg',
      'ring-0',
      'transition-transform',
      'duration-200',
      'ease-in-out'
    ];

    // Size and position variants
    const sizeClasses: Record<ToggleSize, { size: string[]; translate: string[] }> = {
      sm: {
        size: ['h-4', 'w-4'],
        translate: this.checked ? ['translate-x-4'] : ['translate-x-0.5']
      },
      md: {
        size: ['h-5', 'w-5'],
        translate: this.checked ? ['translate-x-6'] : ['translate-x-1']
      },
      lg: {
        size: ['h-6', 'w-6'],
        translate: this.checked ? ['translate-x-7'] : ['translate-x-1']
      }
    };

    const { size, translate } = sizeClasses[this.size()];
    return [...baseClasses, ...size, ...translate].join(' ');
  });

  /** Handle toggle action */
  toggle(): void {
    if (this.isDisabled) { return; }

    const newValue = !this._checked();
    this._checked.set(newValue);
    this.onChangeCallback(newValue);
    this.onTouched();
    this.valueChange.emit(newValue);
    this.toggleChange.emit(newValue);
  }

  /** Handle keyboard interaction */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.toggle();
    }
  }

  // ControlValueAccessor methods
  writeValue(value: boolean): void {
    this._checked.set(value ?? false);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }
}
